import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dashboardAPI, reportsAPI, reportScheduleAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { useSiteStore } from '../stores/siteStore';
import { formatDateTime } from '../utils/formatDateTime';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  DocumentArrowDownIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  TrophyIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const reportTypes = [
  { id: 'daily', name: 'Daily Summary', description: 'Daily safety metrics and alerts', icon: CalendarDaysIcon },
  { id: 'weekly', name: 'Weekly Analysis', description: 'Week-over-week trends', icon: ChartBarIcon },
  { id: 'monthly', name: 'Monthly Executive', description: 'Comprehensive monthly report', icon: DocumentTextIcon },
  { id: 'compliance', name: 'Compliance Report', description: 'PPE compliance breakdown', icon: ShieldCheckIcon },
  { id: 'incident', name: 'Incident Report', description: 'Incident analysis and trends', icon: ExclamationTriangleIcon },
];

export default function ReportsPage() {
  const [dateRange, setDateRange] = useState('7');
  const [selectedReport, setSelectedReport] = useState('daily');
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const { selectedSite } = useSiteStore();

  // Fetch daily stats for charts
  const { data: dailyStats, isLoading } = useQuery({
    queryKey: ['daily-stats', selectedSite?.id, dateRange],
    queryFn: () => dashboardAPI.getDailyStats(selectedSite?.id, parseInt(dateRange)),
  });

  // Fetch leaderboard
  const { data: leaderboard } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => dashboardAPI.getLeaderboard(),
  });

  // Fetch report summary
  const { data: reportSummary } = useQuery({
    queryKey: ['report-summary', selectedSite?.id, dateRange],
    queryFn: () => reportsAPI.getSummary({ siteId: selectedSite?.id, days: parseInt(dateRange) }),
  });

  // Generate report mutation
  const generateReportMutation = useMutation({
    mutationFn: ({ type, params }) => reportsAPI.generateReport(type, params),
    onSuccess: (data) => {
      // Handle successful report generation
      console.log('Report generated:', data);
    },
  });

  // Fetch generated report data
  const { data: generatedReport, isLoading: isGeneratingReport, refetch: refetchReport } = useQuery({
    queryKey: ['generated-report', selectedReport, selectedSite?.id, dateRange],
    queryFn: () => reportsAPI.generateReport(selectedReport, {
      siteId: selectedSite?.id,
      days: parseInt(dateRange),
    }),
    enabled: !!selectedReport,
  });

  const stats = dailyStats?.data || [];
  const sites = leaderboard?.data || [];
  const summary = reportSummary?.data?.summary;
  const report = generatedReport?.data;

  // Export handler
  const handleExport = async (format = 'csv') => {
    setIsExporting(true);
    try {
      const response = await reportsAPI.exportReports({
        siteId: selectedSite?.id,
        days: parseInt(dateRange),
        format,
      });

      if (format === 'csv') {
        // Download CSV file
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `safety-report-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Download JSON file
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `safety-report-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Export specific report type
  const handleExportReport = async (type) => {
    setIsExporting(true);
    try {
      const response = await reportsAPI.generateReport(type, {
        siteId: selectedSite?.id,
        days: parseInt(dateRange),
      });

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Reports & Analytics</h1>
          <p className="text-slate-700 mt-1 font-semibold">Safety performance insights and export</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input w-auto min-w-[150px]"
          >
            <option value="7">Last 7 Days</option>
            <option value="14">Last 14 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExport('csv')}
              disabled={isExporting}
              className="btn btn-secondary flex items-center gap-2"
            >
              {isExporting ? (
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
              ) : (
                <DocumentArrowDownIcon className="w-4 h-4" />
              )}
              Export CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              disabled={isExporting}
              className="btn btn-ghost flex items-center gap-2"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              JSON
            </button>
          </div>
        </div>
      </div>

      {/* Quick Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {reportTypes.map((reportType, index) => (
          <ReportCard
            key={reportType.id}
            report={reportType}
            index={index}
            selected={selectedReport === reportType.id}
            onClick={() => setSelectedReport(reportType.id)}
            onExport={() => handleExportReport(reportType.id)}
            isExporting={isExporting}
          />
        ))}
      </div>

      {/* Report Preview */}
      {report && (
        <div className="glass-card p-6 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <DocumentTextIcon className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {reportTypes.find(r => r.id === selectedReport)?.name} Preview
                </h3>
                <p className="text-sm text-gray-400">
                  Generated at {formatDateTime(report.generatedAt)}
                </p>
              </div>
            </div>
            <button
              onClick={() => refetchReport()}
              className="btn btn-ghost btn-sm flex items-center gap-2"
            >
              <ArrowPathIcon className={clsx('w-4 h-4', isGeneratingReport && 'animate-spin')} />
              Refresh
            </button>
          </div>

          {/* Report Summary Stats */}
          {report.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <ReportStat
                label="Avg Compliance"
                value={`${report.summary.avgOverallCompliance || report.summary.avgComplianceRate || 0}%`}
                color="green"
              />
              <ReportStat
                label="Total Alerts"
                value={report.summary.totalAlerts || 0}
                color="blue"
              />
              <ReportStat
                label="Critical"
                value={report.summary.totalCritical || report.summary.criticalAlertCount || 0}
                color="red"
              />
              <ReportStat
                label="Resolution Rate"
                value={report.summary.totalAlerts > 0
                  ? `${Math.round((report.summary.totalResolved / report.summary.totalAlerts) * 100)}%`
                  : 'N/A'}
                color="purple"
              />
            </div>
          )}

          {/* Report-specific content */}
          {selectedReport === 'weekly' && report.comparison && (
            <WeeklyComparisonCard comparison={report.comparison} />
          )}

          {selectedReport === 'monthly' && report.highlights && (
            <MonthlyHighlightsCard highlights={report.highlights} bySite={report.bySite} />
          )}

          {selectedReport === 'compliance' && report.hourlyDistribution && (
            <ComplianceDistributionCard data={report.hourlyDistribution} />
          )}

          {selectedReport === 'incident' && report.timeline && (
            <IncidentTimelineCard timeline={report.timeline.slice(0, 10)} />
          )}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Average Compliance"
          value={summary?.avgCompliance != null ? `${summary.avgCompliance}%` : stats.length > 0 ? `${Math.round(stats.reduce((acc, s) => acc + (s.ppeCompliance || 0), 0) / stats.length)}%` : 'N/A'}
          trend="up"
          icon={ShieldCheckIcon}
          color="green"
          index={0}
        />
        <SummaryCard
          title="Total Alerts"
          value={summary?.totalAlerts != null ? summary.totalAlerts : stats.length > 0 ? stats.reduce((acc, s) => acc + (s.totalAlerts || 0), 0) : 0}
          icon={ExclamationTriangleIcon}
          color="blue"
          index={1}
        />
        <SummaryCard
          title="Critical Alerts"
          value={summary?.totalCritical != null ? summary.totalCritical : stats.length > 0 ? stats.reduce((acc, s) => acc + (s.criticalAlerts || 0), 0) : 0}
          trend="down"
          icon={ExclamationTriangleIcon}
          color="red"
          index={2}
        />
        <SummaryCard
          title="Avg Response Time"
          value={summary?.avgResponseTime != null ? `${Math.round(summary.avgResponseTime)}s` : 'N/A'}
          icon={ClockIcon}
          color="purple"
          index={3}
        />
      </div>

      {/* Main Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Trend */}
        <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Compliance Trend</h3>
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500/30 border-t-purple-500"></div>
            </div>
          ) : (
            <ComplianceTrendChart data={stats} />
          )}
        </div>

        {/* Alerts Trend */}
        <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Daily Alerts</h3>
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500/30 border-t-purple-500"></div>
            </div>
          ) : (
            <AlertsTrendChart data={stats} />
          )}
        </div>
      </div>

      {/* Site Leaderboard */}
      <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.4s' }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
            <TrophyIcon className="w-5 h-5 text-yellow-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">Site Safety Leaderboard</h3>
        </div>
        {sites.length === 0 ? (
          <div className="text-center py-12 text-slate-700">
            <TrophyIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-semibold">No leaderboard data available</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Site</th>
                  <th>Compliance</th>
                  <th>Days Safe</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site, index) => (
                  <tr key={site.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                    <td>
                      <span className={clsx(
                        'w-8 h-8 rounded-xl flex items-center justify-center font-bold text-sm',
                        index === 0 ? 'bg-yellow-500/20 text-yellow-400 glow-orange' :
                          index === 1 ? 'bg-gray-400/20 text-gray-300' :
                            index === 2 ? 'bg-orange-500/20 text-orange-400' :
                              'bg-white/5 text-gray-500'
                      )}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="font-medium text-white">{site.name}</td>
                    <td>
                      <span className={clsx(
                        'font-semibold',
                        site.todayCompliance >= 90 ? 'text-green-400' :
                          site.todayCompliance >= 70 ? 'text-yellow-400' : 'text-red-400'
                      )}>
                        {site.todayCompliance}%
                      </span>
                    </td>
                    <td className="text-slate-700 font-semibold">{site.daysWithoutIncident}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
                            style={{ width: `${site.safetyScore}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-white w-8">{site.safetyScore}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {/* Scheduled Email Reports */}
      <ReportScheduleSection siteId={selectedSite?.id} />
      </div>
    </div>
  );
}

function ReportScheduleSection({ siteId }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [showTimePickerModal, setShowTimePickerModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [form, setForm] = useState({
    frequency: 'daily',
    reportType: 'daily',
    emails: [],
    newEmail: '',
    hour: null,
    dayOfWeek: 1,
    dayOfMonth: 1
  });
  const [selectedTimeForNewSchedule, setSelectedTimeForNewSchedule] = useState(null);

  const { data: schedules } = useQuery({
    queryKey: ['report-schedules'],
    queryFn: () => reportScheduleAPI.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => reportScheduleAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-schedules'] });
      setShowForm(false);
      resetForm();
      toast.success('✓ Schedule created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create schedule');
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, data }) => reportScheduleAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-schedules'] });
      setEditingSchedule(null);
      resetForm();
      toast.success('✓ Schedule updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update schedule');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => reportScheduleAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-schedules'] });
      toast.success('✓ Schedule deleted');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete schedule');
    },
  });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const resetForm = () => {
    setForm({
      frequency: 'daily',
      reportType: 'daily',
      emails: [],
      newEmail: '',
      hour: null,
      dayOfWeek: 1,
      dayOfMonth: 1
    });
    setSelectedTimeForNewSchedule(null);
  };

  const handleAddEmail = () => {
    const email = form.newEmail.trim();
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (form.emails.includes(email)) {
      toast.error('This email is already added');
      return;
    }
    setForm(f => ({
      ...f,
      emails: [...f.emails, email],
      newEmail: ''
    }));
  };

  const handleRemoveEmail = (emailToRemove) => {
    setForm(f => ({
      ...f,
      emails: f.emails.filter(e => e !== emailToRemove)
    }));
  };

  const handleAddScheduleClick = () => {
    resetForm();
    setEditingSchedule(null);
    setShowTimePickerModal(true);
  };

  const handleTimePickerConfirm = (hour) => {
    setSelectedTimeForNewSchedule(hour);
    setForm(f => ({ ...f, hour }));
    setShowTimePickerModal(false);
    setShowForm(true);
  };

  const handleCancelTimePickerModal = () => {
    setShowTimePickerModal(false);
    setSelectedTimeForNewSchedule(null);
    resetForm();
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    // Parse emails from schedule (can be JSON string or array)
    let emailsList = [];
    if (schedule.emails && Array.isArray(schedule.emails)) {
      emailsList = schedule.emails;
    } else if (schedule.email) {
      try {
        emailsList = JSON.parse(schedule.email);
        if (!Array.isArray(emailsList)) {
          emailsList = [schedule.email];
        }
      } catch (e) {
        emailsList = [schedule.email];
      }
    }

    setForm({
      frequency: schedule.frequency,
      reportType: schedule.reportType,
      emails: emailsList,
      newEmail: '',
      hour: schedule.hour || 8,
      dayOfWeek: schedule.dayOfWeek || 1,
      dayOfMonth: schedule.dayOfMonth || 1,
    });
  };

  const handleSubmit = () => {
    if (form.emails.length === 0) {
      toast.error('Please add at least one recipient email');
      return;
    }

    const submitData = {
      ...form,
      siteId,
      emails: form.emails
    };
    // Remove temporary newEmail field
    delete submitData.newEmail;

    if (editingSchedule) {
      editMutation.mutate({ id: editingSchedule.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const items = schedules?.data?.data || [];

  return (
    <div className="glass-card p-6 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Scheduled Email Reports</h2>
          <p className="text-sm text-slate-700 font-semibold">Automatically receive reports via email at your preferred time</p>
        </div>
        <button
          onClick={() => {
            if (editingSchedule) {
              setEditingSchedule(null);
              resetForm();
              setShowForm(false);
            } else if (showForm) {
              setShowForm(false);
              resetForm();
            } else {
              handleAddScheduleClick();
            }
          }}
          className="btn btn-primary text-sm"
        >
          {showForm || editingSchedule || showTimePickerModal ? 'Cancel' : '+ Add Schedule'}
        </button>
      </div>

      {/* Time Picker Modal */}
      {showTimePickerModal && (
        <TimePickerModal
          onConfirm={handleTimePickerConfirm}
          onCancel={handleCancelTimePickerModal}
        />
      )}

      {(showForm || editingSchedule) && (
        <div className="p-4 bg-white/5 rounded-xl mb-6 space-y-4 border border-white/10">
          {/* Selected Time Display */}
          {form.hour !== null && !editingSchedule && (
            <div className="p-3 bg-purple-500/20 border border-purple-500/30 rounded-lg">
              <p className="text-xs text-slate-700 font-semibold">
                ⏰ Reports will be sent at: <span className="text-purple-600 font-bold">{String(form.hour).padStart(2, '0')}:00</span>
              </p>
            </div>
          )}

          {/* Edit Mode - Show Hour Selector */}
          {editingSchedule && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Send at (Hour)</label>
              <div className="space-y-3">
                {/* Hour Display */}
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-4 text-center">
                  <p className="text-xs text-slate-600 font-semibold mb-1">Selected Time</p>
                  <p className="text-3xl font-bold text-white">
                    {String(form.hour || 0).padStart(2, '0')}:00
                  </p>
                  <p className="text-xs text-slate-700 font-semibold mt-2">
                    {form.hour === 0 ? '🌙 Midnight' : form.hour === 12 ? '☀️ Noon' : form.hour < 12 ? '🌅 Morning' : '🌆 Afternoon/Evening'}
                  </p>
                </div>

                {/* Hour Slider */}
                <div>
                  <input
                    type="range"
                    min="0"
                    max="23"
                    value={form.hour || 0}
                    onChange={(e) => setForm(f => ({ ...f, hour: parseInt(e.target.value) }))}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    style={{
                      background: `linear-gradient(to right, rgb(168, 85, 247) 0%, rgb(168, 85, 247) ${((form.hour || 0) / 23) * 100}%, rgba(255, 255, 255, 0.1) ${((form.hour || 0) / 23) * 100}%, rgba(255, 255, 255, 0.1) 100%)`
                    }}
                  />
                  <p className="text-xs text-slate-700 font-semibold mt-1">Drag slider to adjust time</p>
                </div>

                {/* Quick Select Buttons */}
                <div className="grid grid-cols-4 gap-2">
                  {[0, 6, 12, 18].map(hour => (
                    <button
                      key={hour}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, hour }))}
                      className={clsx(
                        'py-2 px-2 rounded-lg font-semibold text-xs transition-all',
                        (form.hour || 0) === hour
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                          : 'bg-white/10 text-slate-700 hover:bg-white/20'
                      )}
                    >
                      {String(hour).padStart(2, '0')}:00
                    </button>
                  ))}
                </div>

                {/* Hour Input */}
                <div>
                  <label className="block text-xs text-slate-600 font-semibold mb-1">Or enter hour directly (0-23)</label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={form.hour || 0}
                    onChange={(e) => {
                      let value = parseInt(e.target.value);
                      if (isNaN(value)) value = 0;
                      if (value < 0) value = 0;
                      if (value > 23) value = 23;
                      setForm(f => ({ ...f, hour: value }));
                    }}
                    className="input text-sm w-full"
                    placeholder="0-23"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Frequency */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Frequency</label>
              <select
                value={form.frequency}
                onChange={(e) => setForm(f => ({ ...f, frequency: e.target.value }))}
                className="input text-sm w-full"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Report Type */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Report Type</label>
              <select
                value={form.reportType}
                onChange={(e) => setForm(f => ({ ...f, reportType: e.target.value }))}
                className="input text-sm w-full"
              >
                <option value="daily">Safety Summary</option>
                <option value="compliance">Compliance</option>
                <option value="incident">Incidents</option>
              </select>
            </div>

            {/* Day of Week - only for weekly */}
            {form.frequency === 'weekly' && (
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Day of Week</label>
                <select
                  value={form.dayOfWeek || 1}
                  onChange={(e) => setForm(f => ({ ...f, dayOfWeek: parseInt(e.target.value) }))}
                  className="input text-sm w-full"
                >
                  {dayNames.map((day, idx) => (
                    <option key={idx} value={idx}>{day}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Day of Month - only for monthly */}
            {form.frequency === 'monthly' && (
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Day of Month</label>
                <select
                  value={form.dayOfMonth || 1}
                  onChange={(e) => setForm(f => ({ ...f, dayOfMonth: parseInt(e.target.value) }))}
                  className="input text-sm w-full"
                >
                  {Array.from({length: 31}, (_, i) => (
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Recipient Emails */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">Recipient Emails</label>

            {/* Email Input Form */}
            <div className="flex gap-2 mb-3">
              <input
                type="email"
                placeholder="recipient@example.com"
                value={form.newEmail}
                onChange={(e) => setForm(f => ({ ...f, newEmail: e.target.value }))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
                className="input text-sm flex-1 !text-black !bg-white"
              />
              <button
                type="button"
                onClick={handleAddEmail}
                className="btn btn-primary text-sm"
              >
                <PlusIcon className="w-4 h-4" />
                Add
              </button>
            </div>

            {/* Added Emails List */}
            {form.emails.length > 0 ? (
              <div className="space-y-2 mb-3">
                {form.emails.map((email, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg"
                  >
                    <span className="text-sm text-white font-medium">{email}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-all"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-3">
                <p className="text-xs text-amber-700 font-semibold">
                  ℹ️ Add at least one recipient email address
                </p>
              </div>
            )}

            {/* Email Count */}
            <p className="text-xs text-slate-700 font-semibold">
              {form.emails.length} recipient{form.emails.length !== 1 ? 's' : ''} added
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => {
                setShowForm(false);
                setEditingSchedule(null);
                resetForm();
              }}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={form.emails.length === 0 || form.hour === null || createMutation.isPending || editMutation.isPending}
              className="btn btn-primary text-sm"
            >
              {editMutation.isPending || createMutation.isPending ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-1 inline" />
                  {editingSchedule ? 'Updating...' : 'Creating...'}
                </>
              ) : editingSchedule ? 'Update Schedule' : 'Create Schedule'}
            </button>
          </div>

          {/* Schedule Preview */}
          {form.email && validateEmail(form.email) && form.hour !== null && (
            <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <p className="text-xs text-slate-700 font-semibold">
                📅 Preview: {form.frequency === 'daily' ? 'Every day' : form.frequency === 'weekly' ? `Every ${dayNames[form.dayOfWeek || 1]}` : `On the ${form.dayOfMonth || 1}th of each month`} at {String(form.hour).padStart(2, '0')}:00
              </p>
            </div>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-8">
          <CalendarDaysIcon className="w-12 h-12 mx-auto mb-3 text-slate-400 opacity-50" />
          <p className="text-sm text-slate-700 font-semibold mb-3">No scheduled reports yet</p>
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary text-sm inline-flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            Create your first schedule
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((s, idx) => (
            <div
              key={s.id}
              className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/8 transition-colors animate-fade-in"
              style={{ animationDelay: `${idx * 0.05}s` }}
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <CalendarDaysIcon className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-white">
                      {s.frequency.charAt(0).toUpperCase() + s.frequency.slice(1)} {s.reportType} Report
                    </p>
                    <span className={clsx(
                      'text-xs px-2 py-1 rounded-full font-bold',
                      s.isActive !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'
                    )}>
                      {s.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-700 font-semibold mt-2 space-y-1">
                    {/* Emails */}
                    <div>
                      {(() => {
                        let emailsList = [];
                        if (s.emails && Array.isArray(s.emails)) {
                          emailsList = s.emails;
                        } else if (s.email) {
                          try {
                            emailsList = JSON.parse(s.email);
                            if (!Array.isArray(emailsList)) {
                              emailsList = [s.email];
                            }
                          } catch (e) {
                            emailsList = [s.email];
                          }
                        }
                        return emailsList.length > 0 && (
                          <p>
                            📧 {emailsList.join(', ')} {emailsList.length > 1 && `(${emailsList.length} recipients)`}
                          </p>
                        );
                      })()}
                    </div>
                    {/* Schedule time */}
                    <p>
                      🕐 {s.frequency === 'daily' ? 'Every day' : s.frequency === 'weekly' ? `Every ${dayNames[s.dayOfWeek || 1]}` : `Day ${s.dayOfMonth || 1} of month`} at {String(s.hour || 8).padStart(2, '0')}:{String(s.minute || 0).padStart(2, '0')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <button
                  onClick={() => handleEdit(s)}
                  className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-all"
                  title="Edit schedule"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Delete this schedule?')) {
                      deleteMutation.mutate(s.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-50"
                  title="Delete schedule"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportCard({ report, index, selected, onClick, onExport, isExporting }) {
  const Icon = report.icon;
  return (
    <div
      onClick={onClick}
      className={clsx(
        'glass-card p-4 cursor-pointer transition-all duration-300 hover:scale-[1.02] animate-slide-up',
        selected && 'border-purple-500/50 bg-purple-500/10'
      )}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className={clsx(
        'w-10 h-10 rounded-xl flex items-center justify-center mb-3',
        selected ? 'bg-purple-500/30' : 'bg-white/10'
      )}>
        <Icon className={clsx('w-5 h-5', selected ? 'text-purple-400' : 'text-gray-400')} />
      </div>
      <h4 className="font-medium text-white">{report.name}</h4>
      <p className="text-xs text-slate-700 font-semibold mt-1">{report.description}</p>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onExport();
        }}
        disabled={isExporting}
        className="mt-3 text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-colors"
      >
        {isExporting ? (
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
        ) : (
          <DocumentArrowDownIcon className="w-4 h-4" />
        )}
        Export
      </button>
    </div>
  );
}

function SummaryCard({ title, value, trend, icon: Icon, color, index }) {
  const colorConfig = {
    green: { bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400' },
    red: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400' },
    blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' },
    purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400' },
  };

  const config = colorConfig[color];

  return (
    <div
      className="glass-card p-4 transition-all duration-300 hover:scale-[1.02] animate-slide-up"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', config.bg, config.border, 'border')}>
          <Icon className={clsx('w-5 h-5', config.text)} />
        </div>
        {trend && (
          <span className={clsx(
            'text-xs px-2 py-1 rounded-lg',
            trend === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          )}>
            {trend === 'up' ? '↑' : '↓'}
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-slate-700 font-semibold mt-1">{title}</p>
    </div>
  );
}

function ReportStat({ label, value, color }) {
  const colors = {
    green: 'text-green-400',
    blue: 'text-blue-400',
    red: 'text-red-400',
    purple: 'text-purple-400',
  };

  return (
    <div className="bg-white/5 rounded-xl p-4">
      <p className="text-sm text-slate-700 font-semibold">{label}</p>
      <p className={clsx('text-2xl font-bold', colors[color])}>{value}</p>
    </div>
  );
}

function WeeklyComparisonCard({ comparison }) {
  if (!comparison?.current || !comparison?.previous) return null;
  const complianceChange = (comparison.current.avgCompliance ?? 0) - (comparison.previous.avgCompliance ?? 0);
  const alertsChange = (comparison.current.totalAlerts ?? 0) - (comparison.previous.totalAlerts ?? 0);

  return (
    <div className="bg-white/5 rounded-xl p-4">
      <h4 className="text-sm font-medium text-slate-700 font-semibold mb-3">Week-over-Week Comparison</h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-slate-700 font-semibold">Compliance</p>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-white">{comparison.current.avgCompliance}%</span>
            <span className={clsx(
              'text-sm',
              complianceChange >= 0 ? 'text-green-400' : 'text-red-400'
            )}>
              {complianceChange >= 0 ? '+' : ''}{complianceChange}%
            </span>
          </div>
        </div>
        <div>
          <p className="text-sm text-slate-700 font-semibold">Alerts</p>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-white">{comparison.current.totalAlerts}</span>
            <span className={clsx(
              'text-sm',
              alertsChange <= 0 ? 'text-green-400' : 'text-red-400'
            )}>
              {alertsChange >= 0 ? '+' : ''}{alertsChange}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MonthlyHighlightsCard({ highlights, bySite }) {
  return (
    <div className="space-y-4">
      <div className="bg-white/5 rounded-xl p-4">
        <h4 className="text-sm font-medium text-slate-700 font-semibold mb-3">Top Performer</h4>
        {highlights.bestPerformer && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <TrophyIcon className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="font-medium text-white">{highlights.bestPerformer.site?.name}</p>
              <p className="text-sm text-slate-700 font-semibold">{highlights.bestPerformer.avgCompliance}% compliance</p>
            </div>
          </div>
        )}
      </div>
      {bySite && bySite.length > 0 && (
        <div className="bg-white/5 rounded-xl p-4">
          <h4 className="text-sm font-medium text-slate-700 font-semibold mb-3">Site Breakdown</h4>
          <div className="space-y-2">
            {bySite.slice(0, 5).map((site, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-white">{site.site?.name}</span>
                <span className={clsx(
                  'text-sm font-medium',
                  site.avgCompliance >= 90 ? 'text-green-400' :
                    site.avgCompliance >= 70 ? 'text-yellow-400' : 'text-red-400'
                )}>
                  {site.avgCompliance || 0}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ComplianceDistributionCard({ data }) {
  if (!data || data.length === 0) return null;
  const peakHour = data.reduce((max, d) => d.count > max.count ? d : max, data[0]);

  return (
    <div className="bg-white/5 rounded-xl p-4">
      <h4 className="text-sm font-medium text-slate-700 font-semibold mb-3">Violation Distribution by Hour</h4>
      <div className="flex items-end h-32 gap-1">
        {data.map((d, idx) => (
          <div
            key={idx}
            className="flex-1 bg-purple-500/50 rounded-t hover:bg-purple-500/70 transition-colors"
            style={{ height: `${peakHour.count > 0 ? (d.count / peakHour.count) * 100 : 0}%`, minHeight: d.count > 0 ? '4px' : '0' }}
            title={`${d.hour}:00 - ${d.count} violations`}
          />
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs text-slate-700 font-semibold">
        <span>12AM</span>
        <span>6AM</span>
        <span>12PM</span>
        <span>6PM</span>
        <span>12AM</span>
      </div>
      {peakHour.count > 0 && (
        <p className="text-sm text-slate-700 font-semibold mt-3">
          Peak violations at <span className="text-purple-400 font-medium">{peakHour.hour}:00</span> ({peakHour.count} violations)
        </p>
      )}
    </div>
  );
}

function IncidentTimelineCard({ timeline }) {
  return (
    <div className="bg-white/5 rounded-xl p-4">
      <h4 className="text-sm font-medium text-slate-700 font-semibold mb-3">Recent Incidents & Critical Alerts</h4>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {timeline.map((item, idx) => (
          <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-white/5">
            <div className={clsx(
              'w-2 h-2 rounded-full mt-2',
              item.severity === 'CRITICAL' ? 'bg-red-500' :
                item.severity === 'HIGH' ? 'bg-orange-500' :
                  item.severity === 'MEDIUM' ? 'bg-yellow-500' : 'bg-blue-500'
            )} />
            <div className="flex-1">
              <p className="text-sm text-white">{item.type === 'ALERT' ? item.alertType : item.type}</p>
              <p className="text-xs text-slate-700 font-semibold">
                {item.site?.name} • {formatDateTime(item.reportedAt)}
              </p>
            </div>
            <span className={clsx(
              'text-xs px-2 py-1 rounded',
              item.severity === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                item.severity === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-gray-500/20 text-gray-400'
            )}>
              {item.severity}
            </span>
          </div>
        ))}
        {timeline.length === 0 && (
          <p className="text-sm text-slate-700 font-semibold text-center py-4">No incidents or critical alerts</p>
        )}
      </div>
    </div>
  );
}

function ComplianceTrendChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center text-slate-700">
        <ChartBarIcon className="w-12 h-12 mb-3 opacity-50" />
        <p className="font-semibold">No data available</p>
      </div>
    );
  }

  const chartData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: 'Compliance %',
        data: data.map(d => d.ppeCompliance),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: (v) => `${v}%`,
          color: 'rgba(255,255,255,0.5)',
        },
        grid: { color: 'rgba(255,255,255,0.1)' },
      },
      x: {
        ticks: { color: 'rgba(255,255,255,0.5)' },
        grid: { color: 'rgba(255,255,255,0.05)' },
      },
    },
  };

  return (
    <div className="h-64">
      <Line data={chartData} options={options} />
    </div>
  );
}

function AlertsTrendChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center text-slate-700">
        <ChartBarIcon className="w-12 h-12 mb-3 opacity-50" />
        <p className="font-semibold">No data available</p>
      </div>
    );
  }

  const chartData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: 'Total Alerts',
        data: data.map(d => d.totalAlerts),
        backgroundColor: 'rgba(96, 165, 250, 0.8)',
        borderRadius: 4,
      },
      {
        label: 'Critical',
        data: data.map(d => d.criticalAlerts),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: 'rgba(255,255,255,0.7)' },
      },
    },
    scales: {
      y: {
        ticks: { color: 'rgba(255,255,255,0.5)' },
        grid: { color: 'rgba(255,255,255,0.1)' },
      },
      x: {
        ticks: { color: 'rgba(255,255,255,0.5)' },
        grid: { color: 'rgba(255,255,255,0.05)' },
      },
    },
  };

  return (
    <div className="h-64">
      <Bar data={chartData} options={options} />
    </div>
  );
}

function TimePickerModal({ onConfirm, onCancel }) {
  const [selectedHour, setSelectedHour] = useState(8);

  const hours = Array.from({length: 24}, (_, i) => i);

  const handleConfirm = () => {
    onConfirm(selectedHour);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />

      {/* Modal */}
      <div className="relative glass-card w-full max-w-md animate-scale-in p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">
            When should we send reports?
          </h2>
          <p className="text-sm text-slate-700 font-semibold">
            Choose the time of day for your scheduled reports
          </p>
        </div>

        {/* Time Picker */}
        <div className="space-y-4">
          {/* Large Display of Selected Time */}
          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl p-6 text-center">
            <p className="text-xs text-slate-600 font-semibold mb-2">Selected Time</p>
            <p className="text-5xl font-bold text-white">
              {String(selectedHour).padStart(2, '0')}
              <span className="text-2xl">:00</span>
            </p>
            <p className="text-sm text-slate-700 font-semibold mt-2">
              {selectedHour < 12 ? 'AM' : selectedHour === 12 ? 'PM' : 'PM'} • {selectedHour === 0 ? 'Midnight' : selectedHour === 12 ? 'Noon' : selectedHour < 12 ? `${selectedHour}:00 AM` : `${selectedHour - 12}:00 PM`}
            </p>
          </div>

          {/* Time Slider */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-700">
              Drag or click to select time
            </label>
            <input
              type="range"
              min="0"
              max="23"
              value={selectedHour}
              onChange={(e) => setSelectedHour(parseInt(e.target.value))}
              className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
              style={{
                background: `linear-gradient(to right, rgb(168, 85, 247) 0%, rgb(168, 85, 247) ${(selectedHour / 23) * 100}%, rgba(255, 255, 255, 0.1) ${(selectedHour / 23) * 100}%, rgba(255, 255, 255, 0.1) 100%)`
              }}
            />
          </div>

          {/* Quick Select Buttons */}
          <div className="grid grid-cols-4 gap-2">
            {[0, 6, 12, 18].map(hour => (
              <button
                key={hour}
                onClick={() => setSelectedHour(hour)}
                className={clsx(
                  'py-2 px-3 rounded-lg font-semibold text-xs transition-all',
                  selectedHour === hour
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'bg-white/10 text-slate-700 hover:bg-white/20'
                )}
              >
                {String(hour).padStart(2, '0')}:00
              </button>
            ))}
          </div>

          {/* All Hours Grid */}
          <div className="max-h-40 overflow-y-auto">
            <div className="grid grid-cols-6 gap-1">
              {hours.map(hour => (
                <button
                  key={hour}
                  onClick={() => setSelectedHour(hour)}
                  className={clsx(
                    'py-2 px-1 text-xs font-bold rounded transition-all',
                    selectedHour === hour
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                      : 'bg-white/10 text-slate-700 hover:bg-white/20'
                  )}
                  title={`${String(hour).padStart(2, '0')}:00`}
                >
                  {String(hour).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
          <button
            onClick={onCancel}
            className="flex-1 btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 btn btn-primary flex items-center justify-center gap-2"
          >
            <CheckCircleIcon className="w-4 h-4" />
            Confirm Time
          </button>
        </div>
      </div>
    </div>
  );
}
